import React, { useState, useEffect, useMemo } from 'react';
import { FaArrowLeft, FaHeart, FaShare, FaStar, FaShieldAlt, FaTruck, FaCoins, FaPhone, FaComments, FaShoppingCart, FaEye, FaThumbsUp, FaEdit, FaEyeSlash, FaEye as FaEyeIcon } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { productListApiService } from '../../../../service/admin/productApiService';
import { adminApiService } from '../../../../service/admin/adminApiService';
import { fetchToken } from '../../../../lib/globalApiClient';
import './style.css';

const API_URL = process.env.REACT_APP_APIUrl1688;

const ProductDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [productData, setProductData] = useState(null);
  const [selectedSku, setSelectedSku] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [activePriceTab, setActivePriceTab] = useState('wholesale');
  const [translatedSupplier, setTranslatedSupplier] = useState('');
  const [translatedShipping, setTranslatedShipping] = useState('');
  const [calculatedPricing, setCalculatedPricing] = useState(null);
  const [freightCost, setFreightCost] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [tags, setTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [editedTagValue, setEditedTagValue] = useState('');
  const [hiddenTags, setHiddenTags] = useState([]);

  const productFromState = location.state?.product;
  const productFromStateImage = productFromState?.image;
  const offerIdFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('id');
  }, [location.search]);

  const calculatePricing = async (basePrice, quantity) => {
    try {
      const response = await fetch('https://api.importerr.com/api/product/pricing-algorithms/calculate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': '9p1k6lin-fm9l-4jdc-ay8e-241apx2mcger'
        },
        body: JSON.stringify({
          basePrice: parseFloat(basePrice),
          quantity: parseInt(quantity),
          currency: 'RMB'
        })
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Pricing calculation failed:', error);
      return null;
    }
  };

  const calculateFreightCost = async (offerId, skuId, quantity) => {
    try {
      const token = await fetchToken();
      const response = await fetch(`${API_URL}/product-freight-cost`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Token': token
        },
        body: JSON.stringify({
          offerId: offerId.toString(),
          toProvinceCode: "510000",
          toCityCode: "510000",
          toCountryCode: "510000",
          totalNum: parseInt(quantity),
          logisticsSkuNumModels: [{
            skuId: skuId || "default",
            number: parseInt(quantity)
          }]
        })
      });

      const data = await response.json();

      // Call cron-logs API to log this freight cost calculation
      // try {
      //   await adminApiService.post("/cron-logs", {
      //     jobType: 'product-freight-cost',
      //     jobId: `product-freight-cost-${offerId}-${skuId || 'default'}-${Date.now()}`,
      //     status: 'completed',
      //     triggeredBy: 'manual',
      //     domain: 'importerr.com',
      //     targetInfo: {
      //       offerId: offerId.toString(),
      //       skuId: skuId || "default",
      //       quantity: parseInt(quantity)
      //     },
      //     stats: {
      //       totalProducts: 1,
      //       updatedProducts: data?.result?.success ? 1 : 0
      //     },
      //     logs: [{
      //       level: 'info',
      //       message: `Freight cost calculation completed for offerId: ${offerId}, skuId: ${skuId || 'default'}, quantity: ${quantity}`,
      //       timestamp: new Date()
      //     }]
      //   });
      // } catch (logError) {
      //   console.error('Failed to log freight cost calculation:', logError);
      // }

      return data;
    } catch (error) {
      console.error('Freight cost calculation failed:', error);
      return null;
    }
  };

  const processDescriptionHTML = (html) => {
    if (!html) return html;
    // Add referrerPolicy="no-referrer" to all img tags in the description
    return html.replace(/<img([^>]*)>/gi, (match, attributes) => {
      // Check if referrerPolicy is already present
      if (attributes.includes('referrerpolicy') || attributes.includes('referrerPolicy')) {
        return match;
      }
      // Add referrerPolicy="no-referrer" to the img tag
      return `<img${attributes} referrerPolicy="no-referrer">`;
    });
  };

  const translateText = async (text) => {
    try {
      const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=zh&tl=en&dt=t&q=${encodeURIComponent(text)}`;
      const response = await fetch(url);
      const data = await response.json();

      if (data && data[0] && data[0][0] && data[0][0][0]) {
        return data[0][0][0];
      }

      const fallbackUrl = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=zh|en`;
      const fallbackResponse = await fetch(fallbackUrl);
      const fallbackData = await fallbackResponse.json();

      return fallbackData.responseData?.translatedText || text;
    } catch (error) {
      console.error('Translation failed:', error);
      const manualTranslations = {
        '广州': 'Guangzhou',
        '维铭': 'Weiming',
        '表业': 'Watch Industry',
        '商行': 'Trading Company',
        '有限公司': 'Co., Ltd.',
        '贸易': 'Trading',
        '制造': 'Manufacturing',
        '工厂': 'Factory',
        '广东省': 'Guangdong Province',
        '广州市': 'Guangzhou City',
        '深圳市': 'Shenzhen City',
        '东莞市': 'Dongguan City',
        '佛山市': 'Foshan City',
        '中山市': 'Zhongshan City',
        '珠海市': 'Zhuhai City',
        '江苏省': 'Jiangsu Province',
        '浙江省': 'Zhejiang Province',
        '上海市': 'Shanghai City',
        '北京市': 'Beijing City',
        '天津市': 'Tianjin City',
        '重庆市': 'Chongqing City',
        '省': 'Province',
        '市': 'City',
        '区': 'District',
        '县': 'County'
      };

      let translated = text;
      Object.entries(manualTranslations).forEach(([chinese, english]) => {
        translated = translated.replace(new RegExp(chinese, 'g'), english);
      });

      return translated !== text ? translated : text;
    }
  };

  const fetchProductDetail = async (offerId) => {
    setLoading(true);
    try {
      const token = await fetchToken();
      const response = await fetch(`${API_URL}/product-detail-by-offerid`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Token': token
        },
        body: JSON.stringify({
          offerId: offerId.toString(),
          country: 'en'
        })
      });

      const data = await response.json();

      if (data.result?.success) {
        const detail = data.result.result;
        setProductData(detail);
        setSelectedImageIndex(0);
        if (detail.productSkuInfos?.length) {
          setSelectedSku(detail.productSkuInfos[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch product details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    setTagsLoading(true);
    try {
      const response = await productListApiService.getAllTags();
      if (response?.result?.success) {
        setTags(response.result.result?.tags || []);
      }
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    } finally {
      setTagsLoading(false);
    }
  };

  const handleEditTag = (tag) => {
    setEditingTag(tag);
    setEditedTagValue(tag);
  };

  const handleSaveTag = async () => {
    if (!editingTag || !editedTagValue.trim()) return;

    try {
      const response = await productListApiService.updateTag(editingTag, editedTagValue.trim());
      if (response?.result?.success) {
        await fetchTags();
        setEditingTag(null);
        setEditedTagValue('');
        alert('Tag updated successfully');
      } else {
        alert('Failed to update tag: ' + (response?.result?.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Failed to update tag:', error);
      alert('Failed to update tag');
    }
  };

  const handleToggleHideTag = async (tag) => {
    try {
      const response = await productListApiService.toggleHideTag(tag);
      if (response?.result?.success) {
        await fetchTags();
      } else {
        alert('Failed to toggle tag visibility: ' + (response?.result?.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Failed to toggle tag visibility:', error);
      alert('Failed to toggle tag visibility');
    }
  };

  const handleTagClick = (tag) => {
    navigate(`/admin/internal-users/search?keyword=${encodeURIComponent(tag)}`);
  };

  useEffect(() => {
    const offerId = productFromState?.id || offerIdFromQuery;
    if (offerId) {
      fetchProductDetail(offerId);
    } else {
      setLoading(false);
    }

    // Fetch hidden tags
    fetch(process.env.REACT_APP_APIUrl + 'product/hidden-tags')
      .then(res => res.json())
      .then(data => {
        if (data?.status?.code === 200) {
          setHiddenTags(data.data?.hiddenTags || []);
        }
      })
      .catch(err => console.error('Failed to fetch hidden tags:', err));
  }, [productFromState?.id, offerIdFromQuery]);

  useEffect(() => {
    if (productData?.companyName) {
      translateText(productData.companyName).then(setTranslatedSupplier);
    }
    if (productData?.productShippingInfo?.sendGoodsAddressText) {
      translateText(productData.productShippingInfo.sendGoodsAddressText).then(setTranslatedShipping);
    }
  }, [productData]);

  useEffect(() => {
    if (productData?.productSaleInfo?.priceRangeList?.length) {
      const moq = productData.productSaleInfo.priceRangeList[0].startQuantity || 1;
      setQuantity(moq);
    } else {
      setQuantity(1);
    }
  }, [productData]);

  useEffect(() => {
    const updatePricing = async () => {
      if (!productData || !quantity) return;

      setPricingLoading(true);
      const basePrice = selectedSku?.fenxiaoPriceInfo?.offerPrice || productData.productSaleInfo?.priceRangeList?.[0]?.price || 0;

      if (basePrice > 0) {
        const [pricingResult, freightResult] = await Promise.all([
          calculatePricing(basePrice, quantity),
          calculateFreightCost(productData.offerId, selectedSku?.skuId, quantity)
        ]);

        setCalculatedPricing(pricingResult);
        setFreightCost(freightResult);
      }
      setPricingLoading(false);
    };

    updatePricing();
  }, [productData, selectedSku, quantity]);

  const images = useMemo(() => {
    const gallery = productData?.productImage?.images || [];
    const combined = [...gallery];
    if (productFromStateImage) {
      combined.unshift(productFromStateImage);
    }
    const unique = combined.filter(Boolean).filter((image, index, arr) => arr.indexOf(image) === index);
    return unique.length ? unique : ['https://via.placeholder.com/500?text=No+Image'];
  }, [productData, productFromStateImage]);

  useEffect(() => {
    if (!selectedSku && productData?.productSkuInfos?.length) {
      setSelectedSku(productData.productSkuInfos[0]);
    }
  }, [productData, selectedSku]);

  useEffect(() => {
    if (!selectedSku || !images.length) return;
    const attributeImage = selectedSku.skuAttributes?.find(attr => attr.skuImageUrl)?.skuImageUrl;
    const skuImage = selectedSku.skuImageUrl || attributeImage;
    if (skuImage) {
      const index = images.findIndex(img => img === skuImage);
      if (index !== -1) {
        setSelectedImageIndex(index);
      }
    }
  }, [selectedSku, images]);

  const groupedSkus = useMemo(() => {
    const grouped = {};
    productData?.productSkuInfos?.forEach((sku) => {
      sku.skuAttributes?.forEach((attr) => {
        const attributeName = attr.attributeNameTrans || attr.attributeName;
        if (!attributeName) {
          return;
        }
        if (!grouped[attributeName]) {
          grouped[attributeName] = [];
        }
        const valueDisplay = attr.valueTrans || attr.value;
        const existing = grouped[attributeName].some(
          (option) => option.display === valueDisplay || option.raw === attr.value
        );
        if (!existing) {
          grouped[attributeName].push({
            id: `${attributeName}-${valueDisplay}`,
            display: valueDisplay,
            raw: attr.value,
            image: attr.skuImageUrl || attr.imageUrl || attr.valueImageUrl
          });
        }
      });
    });
    return grouped;
  }, [productData]);

  const handleSkuSelection = (attributeName, option) => {
    const matchingSku = productData?.productSkuInfos?.find((sku) =>
      sku.skuAttributes?.some((attr) => {
        const attrName = attr.attributeNameTrans || attr.attributeName;
        const attrValue = attr.valueTrans || attr.value;
        return attrName === attributeName && (attrValue === option.display || attr.value === option.raw);
      })
    );
    if (matchingSku) {
      setSelectedSku(matchingSku);
    }
  };

  const decrementQuantity = () => {
    setQuantity((prev) => Math.max(1, prev - 1));
  };

  const incrementQuantity = () => {
    setQuantity((prev) => {
      const stock = selectedSku?.amountOnSale || Infinity;
      const next = prev + 1;
      return next > stock ? stock : next;
    });
  };

  const handleQuantityInput = (event) => {
    const value = Number(event.target.value);
    if (!Number.isFinite(value) || value <= 0) {
      setQuantity(1);
      return;
    }
    const stock = selectedSku?.amountOnSale || Infinity;
    setQuantity(Math.min(value, stock));
  };

  if (loading) {
    return <div className="loading">Loading product details...</div>;
  }

  if (!productData) {
    return <div className="error">Product not found</div>;
  }

  const supplierDisplayName =
    translatedSupplier && translatedSupplier !== productData.companyName
      ? `${translatedSupplier} (${productData.companyName})`
      : productData.companyName;

  const shippingDisplay =
    translatedShipping && translatedShipping !== productData.productShippingInfo?.sendGoodsAddressText
      ? `${translatedShipping} (${productData.productShippingInfo?.sendGoodsAddressText})`
      : productData.productShippingInfo?.sendGoodsAddressText;

  const keySpecifications = (productData.productAttribute || []).slice(0, 6);
  const moq = Number(productData.productSaleInfo?.priceRangeList?.[0]?.startQuantity) || 1;
  const displayMoq = moq.toLocaleString();

  const formatPrice = (sku) => {
    const priceRanges = productData.productSaleInfo?.priceRangeList || [];
    const basePrice = parseFloat(sku?.fenxiaoPriceInfo?.offerPrice || priceRanges?.[0]?.price || 0);
    const onePiecePrice = parseFloat(sku?.fenxiaoPriceInfo?.onePiecePrice || 0);

    const formatQuantityRangeLabel = (start, end) => {
      if (!start && !end) return '';
      const displayStart = (start || 1).toLocaleString();
      const displayEnd = end ? end.toLocaleString() : '';
      if (!end || end < start) return `≥ ${displayStart} PCS`;
      if (start === end) return `${displayStart} PCS`;
      return `${displayStart}-${displayEnd} PCS`;
    };

    const wholesaleTiers = priceRanges.length
      ? priceRanges.map((range, index) => {
        const startQty = Number(range.startQuantity || 0);
        const endQtyFromApi =
          typeof range.endQuantity !== 'undefined' ? Number(range.endQuantity) : undefined;
        const nextStartQty = Number(priceRanges[index + 1]?.startQuantity || 0);
        const computedEndQty =
          typeof endQtyFromApi === 'number' && !Number.isNaN(endQtyFromApi)
            ? endQtyFromApi
            : nextStartQty > 0
              ? nextStartQty - 1
              : undefined;
        const label = formatQuantityRangeLabel(startQty || 1, computedEndQty);

        return {
          id: `wh-${startQty || index}`,
          label,
          startQuantity: startQty || 1,
          endQuantity: computedEndQty,
          priceRmb: parseFloat(range.price),
          priceInr: parseFloat(range.price) * 12.5
        };
      })
      : basePrice
        ? [
          {
            id: 'wh-1',
            label: '1+ pcs',
            startQuantity: 1,
            endQuantity: null,
            priceRmb: basePrice,
            priceInr: basePrice * 12.5
          }
        ]
        : [];

    const dropshippingTiers = wholesaleTiers.map((tier) => ({
      ...tier,
      id: tier.id.replace('wh', 'ds'),
      priceRmb: tier.priceRmb * 1.15,
      priceInr: tier.priceInr * 1.15
    }));

    const sampleTiers =
      onePiecePrice > 0
        ? [
          {
            id: 'sample-1',
            label: '1 piece (sample)',
            startQuantity: 1,
            endQuantity: 1,
            priceRmb: onePiecePrice,
            priceInr: onePiecePrice * 12.5
          }
        ]
        : [];

    const tiers =
      activePriceTab === 'dropshipping'
        ? dropshippingTiers
        : activePriceTab === 'sample'
          ? sampleTiers
          : wholesaleTiers;

    const minPrice = tiers.length ? Math.min(...tiers.map((tier) => tier.priceRmb)) : 0;
    const maxPrice = tiers.length ? Math.max(...tiers.map((tier) => tier.priceRmb)) : 0;
    const minInr = tiers.length ? Math.min(...tiers.map((tier) => tier.priceInr)) : 0;
    const maxInr = tiers.length ? Math.max(...tiers.map((tier) => tier.priceInr)) : 0;

    const sortedTiers = [...tiers].sort((a, b) => (a.startQuantity || 0) - (b.startQuantity || 0));
    const isWholesale = activePriceTab === 'wholesale';
    const hasWholesaleRanges = isWholesale && priceRanges.length > 0;
    const showTierGrid = isWholesale && sortedTiers.length > 0;
    const effectiveQuantity =
      activePriceTab === 'sample' ? 1 : Math.max(quantity || 1, moq || 1);
    const activeTier =
      sortedTiers.find((tier) => {
        const meetsStart = effectiveQuantity >= (tier.startQuantity || 1);
        const withinEnd = tier.endQuantity ? effectiveQuantity <= tier.endQuantity : true;
        return meetsStart && withinEnd;
      }) || sortedTiers[sortedTiers.length - 1] || null;

    const activeTierLabel = activeTier
      ? formatQuantityRangeLabel(activeTier.startQuantity || moq, activeTier.endQuantity)
      : '';

    const primaryDisplayTier = showTierGrid ? sortedTiers[0] || null : activeTier;
    const singlePanelLabel =
      activePriceTab === 'dropshipping'
        ? 'Dropshipping price (per unit)'
        : 'Sample price (per unit)';

    return (
      <div className="price-module">
        <div className="price-tab-bar">
          <button
            className={`price-tab ${activePriceTab === 'wholesale' ? 'active' : ''}`}
            onClick={() => setActivePriceTab('wholesale')}
          >
            Wholesale
          </button>
          <button
            className={`price-tab ${activePriceTab === 'dropshipping' ? 'active' : ''}`}
            onClick={() => setActivePriceTab('dropshipping')}
          >
            Dropshipping
          </button>
          <button
            className={`price-tab ${activePriceTab === 'sample' ? 'active' : ''}`}
            onClick={() => setActivePriceTab('sample')}
          >
            Sample
          </button>
        </div>

        {tiers.length ? (
          <>
            <div className="price-main">
              <div className="price-primary">
                <span className="price-currency">¥</span>
                <span className="price-value">
                  {(primaryDisplayTier?.priceRmb ?? activeTier?.priceRmb ?? minPrice).toFixed(2)}
                </span>
                {!hasWholesaleRanges && maxPrice > minPrice && (
                  <span className="price-hint">Up to ¥{maxPrice.toFixed(2)}</span>
                )}
              </div>

              {!hasWholesaleRanges && activeTierLabel && (
                <div className="price-range-details">
                  <span className="range-qty">{activeTierLabel}</span>
                  <span className="range-note">MOQ from supplier catalogue</span>
                </div>
              )}
              <span className="price-moq-badge">MOQ {displayMoq} PCS</span>
              <span className="price-note">
                {activePriceTab === 'sample'
                  ? 'Sample price for evaluation orders'
                  : activePriceTab === 'dropshipping'
                    ? 'Inclusive of pick & pack charges for single piece dispatches'
                    : 'Factory wholesale price'}
              </span>
            </div>

            {showTierGrid ? (
              <div className="price-tier-grid">
                {sortedTiers.map((tier) => (
                  <div className="price-tier" key={tier.id}>
                    <span className="tier-qty">{tier.label}</span>
                    <span className="tier-price">¥{tier.priceRmb.toFixed(2)}</span>

                    {tier.startQuantity ? (
                      <span className="tier-moq">
                        MOQ: {tier.startQuantity.toLocaleString()} PCS
                      </span>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : activeTier ? (
              <div className="price-single-card">
                <span className="single-label">{singlePanelLabel}</span>
                <span className="single-price">
                  ¥{(activeTier.priceRmb ?? minPrice).toFixed(2)}
                </span>

                {activeTierLabel && <span className="single-qty">{activeTierLabel}</span>}
                <span className="single-note">
                  {activePriceTab === 'dropshipping'
                    ? 'Includes consolidation and last-mile fulfillment support'
                    : 'Perfect for quality checks and photography samples'}
                </span>
              </div>
            ) : null}
          </>
        ) : (
          <div className="price-empty">Price information is not available</div>
        )}

        {isWholesale && (
          <div className="price-highlights">
            <span>✔ Free shipping on orders over ¥500</span>
            <span>✔ Better prices for larger quantities</span>
            <span>✔ Custom logo & packaging support</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="product-detail-page-1688">
      <div className="product-detail-container">
        {/* 1688-style Header */}
        <div className="detail-header-1688">
          <div className="header-nav">
            <button className="back-btn-1688" onClick={() => navigate(-1)}>
              <FaArrowLeft /> Back to Search
            </button>
            <div className="breadcrumb-1688">
              <span className="breadcrumb-link" onClick={() => navigate('/admin/internal-users/search')}>
                Internal Users
              </span>
              <span className="breadcrumb-separator">&gt;</span>
              <span className="breadcrumb-current">Product Detail</span>
            </div>
          </div>
          <div className="header-actions-1688">
            <button className="action-btn-1688" aria-label="Add to wishlist">
              <FaHeart />
            </button>
            <button className="action-btn-1688" aria-label="Share product">
              <FaShare />
            </button>
          </div>
        </div>

        {/* 1688-style Main Content */}
        <div className="detail-main-1688">
          {/* Left Column - Images */}
          <div className="media-section-1688">
            <div className="media-gallery-1688">
              <div className="main-image-1688">
                <img src={images[selectedImageIndex]} alt={productData.subjectTrans || 'Product image'} referrerPolicy="no-referrer" />
                <div className="image-overlay">
                  <span className="image-counter">{selectedImageIndex + 1}/{images.length}</span>
                </div>
              </div>
              <div className="media-thumbnails-1688">
                {images.map((img, index) => (
                  <button
                    type="button"
                    key={img}
                    className={`thumbnail-1688 ${selectedImageIndex === index ? 'active' : ''}`}
                    onClick={() => setSelectedImageIndex(index)}
                  >
                    <img src={img} alt={`Preview ${index + 1}`} referrerPolicy="no-referrer" />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Center Column - Product Info */}
          <div className="info-section-1688">
            <h1 className="product-title-1688">{productData.subjectTrans || productData.subject}</h1>

            <div className="product-meta-1688">
              <div className="rating-section">
                <span className="rating-stars">
                  <FaStar className="star-filled" />
                  <FaStar className="star-filled" />
                  <FaStar className="star-filled" />
                  <FaStar className="star-filled" />
                  <FaStar className="star-empty" />
                </span>
                <span className="rating-score">{productData.tradeScore || '4.2'}</span>
                <span className="rating-count">({productData.soldOut || '156'} reviews)</span>
              </div>
              <div className="sales-info">
                <span className="sales-count">{productData.soldOut || '2,847'} sold</span>
                <span className="monthly-sales">{productData.monthSold || '156'} monthly orders</span>
              </div>
            </div>

            <div className="price-section-1688">
              {formatPrice(selectedSku || {})}
            </div>

            <div className="service-tags-1688">
              <span className="service-tag trade-assurance">
                <FaShieldAlt /> Trade Assurance
              </span>
              <span className="service-tag ready-ship">
                <FaTruck /> Ready to Ship
              </span>
              <span className="service-tag fast-delivery">
                48h Dispatch
              </span>
            </div>

            {/* Supplier Info - 1688 Style */}
            <div className="supplier-info-1688">
              <div className="supplier-header">
                <div className="supplier-avatar">
                  <img src="https://via.placeholder.com/48x48?text=S" alt="Supplier" />
                </div>
                <div className="supplier-details">
                  <h3 className="supplier-name">{supplierDisplayName || 'Verified Supplier'}</h3>
                  <div className="supplier-badges">
                    <span className="badge verified">Verified</span>
                    <span className="badge gold">Gold Supplier</span>
                  </div>
                </div>
                <div className="supplier-actions">
                  <button className="contact-btn">
                    <FaComments /> Contact
                  </button>
                </div>
              </div>
              <div className="supplier-stats">
                <div className="stat-item">
                  <span className="stat-label">Response Rate</span>
                  <span className="stat-value">98%</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Response Time</span>
                  <span className="stat-value">&lt; 2h</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Location</span>
                  <span className="stat-value">{translatedShipping || 'Guangzhou'}</span>
                </div>
              </div>
            </div>

            {/* Product Variations - 1688 Style */}
            {Object.entries(groupedSkus).map(([attributeName, options]) => (
              <div className="variation-section-1688" key={attributeName}>
                <div className="variation-header">
                  <span className="variation-label">{attributeName}:</span>
                </div>
                <div className="variation-options">
                  {options.map((option) => {
                    const isSelected = selectedSku?.skuAttributes?.some((attr) => {
                      const attrName = attr.attributeNameTrans || attr.attributeName;
                      const attrValue = attr.valueTrans || attr.value;
                      return attrName === attributeName && (attrValue === option.display || attr.value === option.raw);
                    });

                    return (
                      <button
                        type="button"
                        key={option.id}
                        className={`variation-option ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleSkuSelection(attributeName, option)}
                      >
                        {option.image && (
                          <div className="option-image">
                            <img src={option.image} alt={option.display} referrerPolicy="no-referrer" />
                          </div>
                        )}
                        <span className="option-text">{option.display}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Quantity Selector with INR Price - 1688 Style */}
            <div className="quantity-section-1688">
              <div className="quantity-row">
                <span className="quantity-label">Quantity:</span>
                <div className="quantity-controls-1688">
                  <button type="button" onClick={decrementQuantity} className="qty-btn">-</button>
                  <input type="number" min="1" value={quantity} onChange={handleQuantityInput} className="qty-input" />
                  <button type="button" onClick={incrementQuantity} className="qty-btn">+</button>
                </div>
                <span className="stock-info">
                  {selectedSku?.amountOnSale ? `${selectedSku.amountOnSale} pieces available` : 'Stock available'}
                </span>
              </div>
              <div className="quantity-pricing">
                <div className="total-price-display">
                  <span className="total-label">Total Price:</span>
                  <div className="price-breakdown">
                    {pricingLoading ? (
                      <span className="pricing-loading">Calculating...</span>
                    ) : (
                      <>
                        <span className="rmb-total">¥{((selectedSku?.fenxiaoPriceInfo?.offerPrice || productData.productSaleInfo?.priceRangeList?.[0]?.price || 0) * quantity).toFixed(2)}</span>
                        <span className="inr-total">₹{((calculatedPricing?.finalPrice ? calculatedPricing.finalPrice / quantity : (selectedSku?.fenxiaoPriceInfo?.offerPrice || productData.productSaleInfo?.priceRangeList?.[0]?.price || 0) * 12.5) * quantity).toFixed(2)}</span>
                        {freightCost?.freight && (
                          <span className="freight-cost">+ ₹{freightCost.freight.toFixed(2)} shipping</span>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="moq-info">
                  <span>MOQ: {displayMoq} pieces</span>
                  {calculatedPricing?.margin && (
                    <span className="margin-info">Margin: {calculatedPricing.margin}%</span>
                  )}
                </div>
              </div>
            </div>

            {keySpecifications.length > 0 && (
              <div className="spec-preview-1688">
                <div className="spec-header-1688">
                  <span className="spec-title-1688">Product Details</span>
                  <button className="view-all-link" onClick={() => setActiveTab('specifications')}>
                    View all &gt;
                  </button>
                </div>
                <div className="spec-list-1688">
                  {keySpecifications.slice(0, 4).map((spec, index) => (
                    <div className="spec-row-1688" key={`${spec.attributeNameTrans}-${index}`}>
                      <span className="spec-name">{spec.attributeNameTrans}:</span>
                      <span className="spec-val">{spec.valueTrans}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>

          {/* Right Column - Purchase Section */}
          <div className="purchase-section-1688">
            <div className="purchase-card">
              <div className="purchase-header">
                <h3>Purchase Options</h3>
              </div>

              <div className="purchase-actions-1688">
                <button
                  className="btn-primary-1688 find-similar"
                  onClick={() =>
                    navigate('/admin/internal-users/find-similar', {
                      state: {
                        product: {
                          id: productData.offerId,
                          title: productData.subjectTrans || productData.subject,
                          image: images[0],
                          price: {
                            rmb: parseFloat(selectedSku?.fenxiaoPriceInfo?.offerPrice || productData.productSaleInfo?.priceRangeList?.[0]?.price || 0),
                            inr: (calculatedPricing?.finalPrice ? calculatedPricing.finalPrice / quantity : parseFloat(selectedSku?.fenxiaoPriceInfo?.offerPrice || productData.productSaleInfo?.priceRangeList?.[0]?.price || 0) * 12.5)
                          },
                          supplier: productData.companyName
                        }
                      }
                    })
                  }
                >
                  <FaEye /> Find Similar
                </button>
              </div>

              <div className="service-promises">
                <div className="promise-item">
                  <FaShieldAlt className="promise-icon" />
                  <div className="promise-text">
                    <strong>Trade Assurance</strong>
                    <span>Protect your orders</span>
                  </div>
                </div>
                <div className="promise-item">
                  <FaTruck className="promise-icon" />
                  <div className="promise-text">
                    <strong>Fast Delivery</strong>
                    <span>Ships within 48 hours</span>
                  </div>
                </div>
                <div className="promise-item">
                  <FaCoins className="promise-icon" />
                  <div className="promise-text">
                    <strong>Flexible MOQ</strong>
                    <span>From {displayMoq} pieces</span>
                  </div>
                </div>
              </div>

              <div className="shipping-info-1688">
                <h4>Shipping & Payment</h4>
                <div className="shipping-details">
                  <div className="detail-row">
                    <span>Shipping from:</span>
                    <span>{shippingDisplay || 'Guangzhou, China'}</span>
                  </div>
                  <div className="detail-row">
                    <span>Lead time:</span>
                    <span>3-7 days (sample), 15-30 days (bulk)</span>
                  </div>
                  <div className="detail-row">
                    <span>Payment:</span>
                    <span>T/T, L/C, Trade Assurance</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* 1688-style Tabs Section */}
        <div className="product-tabs-1688">
          <div className="tab-navigation-1688">
            <button
              className={`tab-btn-1688 ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              Product Details
            </button>
            <button
              className={`tab-btn-1688 ${activeTab === 'specifications' ? 'active' : ''}`}
              onClick={() => setActiveTab('specifications')}
            >
              Specifications
            </button>
            <button
              className={`tab-btn-1688 ${activeTab === 'tags' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('tags');
                fetchTags();
              }}
            >
              Tags
            </button>
            <button
              className={`tab-btn-1688 ${activeTab === 'pricing' ? 'active' : ''}`}
              onClick={() => setActiveTab('pricing')}
            >
              Pricing & MOQ
            </button>
            <button
              className={`tab-btn-1688 ${activeTab === 'company' ? 'active' : ''}`}
              onClick={() => setActiveTab('company')}
            >
              Company Profile
            </button>
            <button
              className={`tab-btn-1688 ${activeTab === 'shipping' ? 'active' : ''}`}
              onClick={() => setActiveTab('shipping')}
            >
              Shipping & Payment
            </button>
          </div>

          <div className="tab-content-1688">
            {activeTab === 'details' && (
              <div className="tab-panel-1688">
                <div className="description-section-1688">
                  <h3>Product Description</h3>
                  <div
                    className="description-content-1688"
                    dangerouslySetInnerHTML={{
                      __html: processDescriptionHTML(productData.description) || '<p>High-quality product with excellent craftsmanship and reliable performance. Suitable for various applications and backed by our quality guarantee.</p>'
                    }}
                  />
                </div>

                <div className="trade-assurance-section-1688">
                  <h3>Trade Assurance</h3>
                  <div className="assurance-grid-1688">
                    <div className="assurance-card-1688">
                      <div className="assurance-icon-1688">
                        <FaShieldAlt />
                      </div>
                      <div className="assurance-content-1688">
                        <strong>Product Quality Protection</strong>
                        <p>Get refund if products don't match description</p>
                      </div>
                    </div>
                    <div className="assurance-card-1688">
                      <div className="assurance-icon-1688">
                        <FaTruck />
                      </div>
                      <div className="assurance-content-1688">
                        <strong>On-time Delivery Protection</strong>
                        <p>Get refund if delivery is delayed</p>
                      </div>
                    </div>
                    <div className="assurance-card-1688">
                      <div className="assurance-icon-1688">
                        <FaCoins />
                      </div>
                      <div className="assurance-content-1688">
                        <strong>Payment Protection</strong>
                        <p>Secure payment with multiple options</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'specifications' && (
              <div className="tab-panel-1688">
                <div className="specifications-section-1688">
                  <h3>Product Specifications</h3>
                  <div className="specs-table-1688">
                    {productData.productAttribute?.filter((attr) => {
                      const label = (attr.attributeNameTrans || attr.attributeName || '').toLowerCase();
                      const attrId = (attr.attributeId || '').toString().toLowerCase();
                      // Hide Supplierlogo and Consign Price
                      if (label.includes('supplierlogo') || label.includes('supplier logo')) return false;
                      if (label.includes('consign price') || label.includes('consignprice')) return false;
                      // Check if this is keywordSearch attribute
                      if (attrId === 'keywordsearch' || label.includes('keyword search')) {
                        // Show keywordSearch but make it special (clickable)
                        return true;
                      }
                      return true;
                    }).map((attr, index) => {
                      const label = (attr.attributeNameTrans || attr.attributeName || '').toLowerCase();
                      const attrId = (attr.attributeId || '').toString().toLowerCase();
                      const isKeywordSearch = attrId === 'keywordsearch' || label.includes('keyword search');
                      let tagValue = attr.valueTrans || attr.value || '';

                      // If this is keywordSearch and value is a comma-separated string, filter hidden tags
                      if (isKeywordSearch && tagValue) {
                        const tags = tagValue.split(',').map(t => t.trim()).filter(t => t);
                        const visibleTags = tags.filter(tag => !hiddenTags.includes(tag));
                        tagValue = visibleTags.join(', ');
                        if (!tagValue) return null; // Don't show if all tags are hidden
                      }

                      return (
                        <div key={`${attr.attributeNameTrans}-${index}`} className="spec-row-1688">
                          <span className="spec-label-1688">{attr.attributeNameTrans}</span>
                          <span className={`spec-value-1688 ${isKeywordSearch ? 'clickable-tag' : ''}`}>
                            {isKeywordSearch && tagValue ? (
                              <span
                                className="tag-value-clickable"
                                onClick={() => handleTagClick(tagValue)}
                                style={{ cursor: 'pointer', color: '#1890ff', textDecoration: 'underline' }}
                              >
                                {tagValue}
                              </span>
                            ) : (
                              tagValue
                            )}
                          </span>
                        </div>
                      );
                    }).filter(Boolean)}
                  </div>
                </div>

                <div className="packaging-section-1688">
                  <h3>Packaging & Delivery</h3>
                  <div className="packaging-grid-1688">
                    <div className="packaging-item-1688">
                      <strong>Packaging Details:</strong>
                      <span>{productData.productShippingInfo?.packageType || 'Standard export packaging'}</span>
                    </div>
                    <div className="packaging-item-1688">
                      <strong>Delivery Time:</strong>
                      <span>{productData.productShippingInfo?.deliveryTime || '7-15 days after payment'}</span>
                    </div>
                    <div className="packaging-item-1688">
                      <strong>Port:</strong>
                      <span>{productData.productShippingInfo?.sendGoodsAddressText || 'Ningbo / Shenzhen'}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'tags' && (
              <div className="tab-panel-1688">
                <div className="tags-section-1688">
                  <h3>All Tags (keywordSearch)</h3>
                  {tagsLoading ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}>Loading tags...</div>
                  ) : (
                    <div className="tags-table-1688">
                      <div className="tags-header-1688">
                        <span>Tag</span>
                        <span>Actions</span>
                      </div>
                      {tags.length === 0 ? (
                        <div style={{ padding: '20px', textAlign: 'center' }}>No tags found</div>
                      ) : (
                        tags.map((tagObj, index) => {
                          const tag = tagObj.tag || tagObj;
                          const isHidden = tagObj.isHidden || false;
                          const isEditing = editingTag === tag;

                          return (
                            <div
                              key={index}
                              className={`tag-row-1688 ${isHidden ? 'hidden-tag' : ''}`}
                              style={{ opacity: isHidden ? 0.5 : 1 }}
                            >
                              <span className="tag-value-1688">
                                {isEditing ? (
                                  <input
                                    type="text"
                                    value={editedTagValue}
                                    onChange={(e) => setEditedTagValue(e.target.value)}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleSaveTag();
                                      } else if (e.key === 'Escape') {
                                        setEditingTag(null);
                                        setEditedTagValue('');
                                      }
                                    }}
                                    style={{ padding: '5px', width: '100%', maxWidth: '400px' }}
                                    autoFocus
                                  />
                                ) : (
                                  <span
                                    className="clickable-tag"
                                    onClick={() => handleTagClick(tag)}
                                    style={{ cursor: 'pointer', color: '#1890ff' }}
                                  >
                                    {tag}
                                  </span>
                                )}
                              </span>
                              <span className="tag-actions-1688">
                                {isEditing ? (
                                  <>
                                    <button
                                      onClick={handleSaveTag}
                                      className="tag-action-btn save-btn"
                                      style={{ marginRight: '5px' }}
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => {
                                        setEditingTag(null);
                                        setEditedTagValue('');
                                      }}
                                      className="tag-action-btn cancel-btn"
                                    >
                                      Cancel
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => handleEditTag(tag)}
                                      className="tag-action-btn edit-btn"
                                      style={{ marginRight: '5px' }}
                                      title="Edit tag"
                                    >
                                      <FaEdit />
                                    </button>
                                    <button
                                      onClick={() => handleToggleHideTag(tag)}
                                      className="tag-action-btn hide-btn"
                                      title={isHidden ? 'Show tag' : 'Hide tag'}
                                    >
                                      {isHidden ? <FaEyeIcon /> : <FaEyeSlash />}
                                    </button>
                                  </>
                                )}
                              </span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'pricing' && (
              <div className="tab-panel-1688">
                <div className="pricing-section-1688">
                  <h3>Bulk Pricing Tiers</h3>
                  <div className="pricing-table-1688">
                    <div className="pricing-header-1688">
                      <span>Quantity</span>
                      <span>Unit Price (RMB)</span>

                      <span>Savings</span>
                    </div>
                    {productData.productSaleInfo?.priceRangeList?.map((range, index) => {
                      const basePrice = parseFloat(
                        productData.productSaleInfo.priceRangeList[0].price || 0
                      );
                      const rangePrice = parseFloat(range.price || 0);
                      const savings =
                        index > 0 && basePrice
                          ? (((basePrice - rangePrice) / basePrice) * 100).toFixed(1)
                          : '0';
                      return (
                        <div key={`${range.startQuantity}-${index}`} className="pricing-row-1688">
                          <span className="qty-range">{range.startQuantity}+ pieces</span>
                          <span className="price-rmb">¥{rangePrice.toFixed(2)}</span>

                          <span className="savings-pct">{savings}% off</span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="pricing-benefits-1688">
                    <div className="benefit-card">
                      <span className="benefit-emoji">🚚</span>
                      <span>Free shipping on orders over ¥500</span>
                    </div>
                    <div className="benefit-card">
                      <span className="benefit-emoji">💰</span>
                      <span>Better prices for larger quantities</span>
                    </div>
                    <div className="benefit-card">
                      <span className="benefit-emoji">🎯</span>
                      <span>Customization available</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'company' && (
              <div className="tab-panel-1688">
                <div className="company-section-1688">
                  <h3>Company Information</h3>
                  <div className="company-card-1688">
                    <div className="company-header-1688">
                      <div className="company-logo">
                        <img src="https://via.placeholder.com/80x80?text=LOGO" alt="Company Logo" />
                      </div>
                      <div className="company-basic">
                        <h4>{productData.companyName}</h4>
                        <p>{productData.supplierDetail?.businessType || 'Manufacturer & Trading Company'}</p>
                        <div className="company-badges">
                          <span className="badge verified">Verified Supplier</span>
                          <span className="badge gold">Gold Member</span>
                        </div>
                      </div>
                    </div>
                    <div className="company-stats-1688">
                      <div className="stat-card">
                        <span className="stat-number">{productData.tradeScore || '4.8'}</span>
                        <span className="stat-label">Trade Score</span>
                      </div>
                      <div className="stat-card">
                        <span className="stat-number">{productData.soldOut || '2,847'}</span>
                        <span className="stat-label">Total Sales</span>
                      </div>
                      <div className="stat-card">
                        <span className="stat-number">98%</span>
                        <span className="stat-label">Response Rate</span>
                      </div>
                    </div>
                    <div className="company-details-1688">
                      <div className="detail-item">
                        <strong>Location:</strong>
                        <span>{shippingDisplay || productData.productShippingInfo?.sendGoodsAddressText}</span>
                      </div>
                      <div className="detail-item">
                        <strong>Main Products:</strong>
                        <span>Electronics, Consumer Goods, Industrial Equipment</span>
                      </div>
                      <div className="detail-item">
                        <strong>Established:</strong>
                        <span>2015</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'shipping' && (
              <div className="tab-panel-1688">
                <div className="shipping-section-1688">
                  <h3>Shipping Information</h3>
                  <div className="shipping-grid-1688">
                    <div className="shipping-card">
                      <h4>Shipping Methods</h4>
                      <div className="method-list">
                        <div className="method-item">
                          <span className="method-name">Express Delivery</span>
                          <span className="method-time">3-7 days</span>
                        </div>
                        <div className="method-item">
                          <span className="method-name">Air Freight</span>
                          <span className="method-time">7-15 days</span>
                        </div>
                        <div className="method-item">
                          <span className="method-name">Sea Freight</span>
                          <span className="method-time">15-30 days</span>
                        </div>
                      </div>
                    </div>
                    <div className="payment-card">
                      <h4>Payment Methods</h4>
                      <div className="payment-methods">
                        <span className="payment-method">T/T</span>
                        <span className="payment-method">L/C</span>
                        <span className="payment-method">PayPal</span>
                        <span className="payment-method">Trade Assurance</span>
                      </div>
                      <div className="payment-terms">
                        <p><strong>Terms:</strong> 30% deposit, 70% before shipment</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;